import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CardsStackIcon, CloseIcon, HomeIcon, LogoutIcon, ReceiptShortIcon } from '../svgs/ExpressonSVGs';

type Props = {
	disable?: boolean,
	activeTab: number //Active tab is to indicate the current active menu element. It refers to the menu id found in `menuItems`
}

interface MenuItem {
	id: number;
	title: string;
	path: string;
	hasSubmenu: boolean;
	submenu?: SubMenuItem[];
}

interface SubMenuItem {
	id: number;
	title: string;
	path: string;
	information?: string;
}

interface MenuIcons {
	id: number;
	html: string;
}

const MobileMenuAccessable: React.FC<Props> = ({ disable, activeTab }) => {

	const [activeMenu, setActiveMenu] = useState<string | null>(null); // Single state to track active menu

	// Assume all card types are available for menu display, cards page will always be available but will show appropriate messaging if no cards are active
	const customer = {
		hasActivePrepaidCard: true,
		hasActiveCreditCard: true,
		bankCardCustomer: true
	};
	/**
	 * 
	 * @param pathname provide the current url path
	 * @returns 
	 */
	const getActiveSubMenuPath = (pathname: string): number => {
		const pathTabMapping: { [key: string]: number } = {
			'/mitt-konto/dashboard': 1,
			'/transaktioner': 2,
			'/kort': 3,
			'/loggaut': 4
		};

		return pathTabMapping[pathname] || 0
	}

	/**
	 * Get all paths associated with a menu item
	 * Menu item can be active for multiple routes
	 */
	const getMenuItemActivePaths = (menuId: number): string[] => {
		const pathMapping: { [key: number]: string[] } = {
			1: [
				"/mitt-konto/dashboard",
				"/mitt-konto/fakturor-kontobesked",
				"/mitt-konto/hantera-anvandare",
				"/mitt-konto/installningar"
			],
			2: ["/transaktioner"],
			3: ["/kort"],
			4: ["/loggaut"]
		};

		return pathMapping[menuId] || [];
	};

	/**
	 * Check if a menu item should be active for the current path
	 */
	const isMenuItemActive = (menuId: number): boolean => {
		const activePaths = getMenuItemActivePaths(menuId);
		return activePaths.includes(location.pathname);
	};
	/**
	 * Toggle menu state when a parent menu with submenus is clicked
	 * @param type - menu type/key 
	 * @param event - mouse click event
	 */
	const parentMenuActive = (type: string, event?: React.MouseEvent<HTMLLIElement>) => {
		if (event) event.stopPropagation();

		// Toggle between opening/closing the same menu, or switching between menus
		setActiveMenu((prevMenu) => prevMenu === type ? null : type);
	};

	/**
	 * Resets all open menus
	 */
	const resetSubmenu = () => {
		setActiveMenu(null); // Clear active menu
	};

	/**
	 * Handle clicks in the old mobile menu so it closes the submenu when the old menu routing links are used
	 */
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			// Prevent resetting if clicking inside a menu or submenu
			const target = event.target as Element;
			if (target.closest('.menu-item-has-children') || target.closest('.submenu')) return;

			resetSubmenu();
		};

		document.addEventListener('click', handleClick);
		return () => {
			document.removeEventListener('click', handleClick);
		};
	}, []);


	/**
	 * Renders menu item icon - returns React component for specific icons, SVG string for others
	 */
	const renderMenuIcon = (menuId: number) => {
		if (menuId === 1) {
			return <HomeIcon width={28} height={28} />;
		}

		if (menuId === 2) {
			return <ReceiptShortIcon width={28} height={28} />;
		}

		if (menuId === 3) {
			return <CardsStackIcon width={28} height={28} />;
		}

		if (menuId === 4) {
			return <LogoutIcon width={28} height={28} />;
		}

		return <CloseIcon width={28} height={28} />; // Fallback if something changes there will be a close icon
	};

	/**
	 * Dynamically generate CSS classes based on state
	 */
	const getClassNames = (menuId: number): string => {
		const classes = [
			'menu-has-children',
			isMenuItemActive(menuId) ? 'active' : '',
			activeMenu === 'transactions' && menuId === 2 ? 'open transactions' : '',
			activeMenu === 'settings' && menuId === 4 ? 'open settings' : '',
		].filter(Boolean);

		return classes.join(' ');
	};

	/**
	 * Render the menu items, including parent and child menus
	 */
	const parseMenuItem = (menuObject: MenuItem[]) => (
		<Fragment>
			<ul className="parent-menu">
				{menuObject.map((primaryMenuItem) => (
					<React.Fragment key={primaryMenuItem.id}>
						{!primaryMenuItem.hasSubmenu && (
							<li
								className={isMenuItemActive(primaryMenuItem.id) ? 'active' : ''}
							>
								<Link
									to={primaryMenuItem.path}
									tabIndex={1}
									data-has-children="false"
									aria-label={primaryMenuItem.title}
								>
									{renderMenuIcon(primaryMenuItem.id)}
								</Link>
							</li>
						)}

						{primaryMenuItem.hasSubmenu && (
							<li
								className={getClassNames(primaryMenuItem.id)}
								aria-label={primaryMenuItem.title}
								data-has-children="true"
								onClick={(event) =>
									parentMenuActive(
										primaryMenuItem.id === 2 ? 'transactions' : 'settings',
										event
									)
								}
							>
								<div
									className="menu-item-has-children"
									tabIndex={1}
								>
									{renderMenuIcon(primaryMenuItem.id)}
								</div>
								<ul
									className='profile-settings'
								>
									{primaryMenuItem.submenu!.map((subMenuItem) => (
										<li
											key={subMenuItem.id}
											aria-label={subMenuItem.title}
											className={
												subMenuItem.id === getActiveSubMenuPath(location.pathname) ? 'active' : ''
											}
										>
											<Link
												aria-label={`${subMenuItem.title} länk`}
												to={subMenuItem.path}
												tabIndex={-1}
											>
												{subMenuItem.title}
											</Link>
										</li>
									))}
								</ul>
							</li>
						)}
					</React.Fragment>
				))}
			</ul>
		</Fragment>
	);

	/**
	 * Menu item definitions
	 */
	const menuItems: MenuItem[] = [
		{
			id: 1,
			title: "Översikt",
			path: "/mitt-konto/dashboard",
			hasSubmenu: false
		},
		{
			id: 2,
			title: "Transaktioner",
			path: "/transaktioner",
			hasSubmenu: false,
		},
		{
			id: 3,
			title: "Kortöversikt",
			path: "/kort",
			hasSubmenu: false,
		},
		{
			id: 4,
			title: "Logga ut",
			path: "/loggaut",
			hasSubmenu: false,
		}
	];

	// Render the menu
	return <nav className="mobile floating-menu" aria-label="Mobile navigation menu">{parseMenuItem(menuItems)}</nav>;
};

export default MobileMenuAccessable;